import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { type MenuItem, type MobileSection } from '@/types/common';
import useAuth from '@/components/UserContext';
import getInitials from '@/utils/getInitials';
import NavigationLink from '@/components/Navigation/NavigationLink';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcChevronRight from '@/assets/icons/ChevronRight.svg?react';
import styles from './MobileMoreSheet.module.scss';

interface MobileMoreSheetProps {
  open: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
}

const SECTIONS: { key: MobileSection; labelKey: string }[] = [
  { key: 'workspace', labelKey: 'common:menu.section.workspace' },
  { key: 'tools', labelKey: 'common:menu.section.tools' },
  { key: 'profile', labelKey: 'common:menu.section.profile' },
  { key: 'help', labelKey: 'common:menu.section.help' },
];

const MobileMoreSheet = ({
  open,
  onClose,
  menuItems,
}: MobileMoreSheetProps) => {
  const { t } = useTranslation(['common']);
  const { userData } = useAuth();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const flattenedItems = menuItems.flatMap((item) =>
    item.submenuItems?.length ? [item, ...item.submenuItems] : [item]
  );

  const sectionsWithItems = SECTIONS.map(({ key, labelKey }) => ({
    key,
    labelKey,
    items: flattenedItems.filter((item) => item.mobileSection === key),
  })).filter((section) => section.items.length > 0);

  const logoutItem = flattenedItems.find((item) => item.action === 'logout');

  const displayName = userData.name || userData.email || '';
  const initials = getInitials(displayName);

  return (
    <>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className={classNames(styles.scrim, { [styles.scrimOpen]: open })}
        onClick={onClose}
        data-testid="more-sheet-scrim"
      />
      <div
        className={classNames(styles.sheet, { [styles.sheetOpen]: open })}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <div className={styles.grab} />
        <div className={styles.head}>
          <div className={styles.user}>
            <span className={styles.avatar}>{initials}</span>
            <span className={styles.userMeta}>
              <span className={styles.name}>
                {userData.name || t('common:menu.account')}
              </span>
              <span className={styles.mail}>{userData.email}</span>
            </span>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('common:menu.close')}
          >
            <IcCross />
          </button>
        </div>

        {sectionsWithItems.map(({ key, labelKey, items }) => (
          <div key={key} className={styles.section}>
            <div className={styles.sectionLabel}>{t(labelKey)}</div>
            {items.map((item) => (
              <NavigationLink
                key={item.label + item.href}
                action={item.action}
                highlight={item.highlight}
                href={item.href}
                Icon={item.icon}
                TrailingIcon={IcChevronRight}
                label={item.label}
                refreshOnSameClick={item.refreshOnSameClick}
                target={item.target}
                onClick={onClose}
                className={styles.row}
              />
            ))}
          </div>
        ))}

        {logoutItem && (
          <div className={styles.logoutWrap}>
            <NavigationLink
              action={logoutItem.action}
              highlight={logoutItem.highlight}
              href={logoutItem.href}
              Icon={logoutItem.icon}
              label={logoutItem.label}
              onClick={onClose}
              className={styles.row}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default MobileMoreSheet;
